<template>
  <ReportCellWrapper
    :to="params.value.reportLink"
    :trial-over="params.value.trialOver"
  >
    <div class="flex flex-col">
      <span
        v-if="
          !hasCombinedCreateAndSavedReports || type === COL_TYPES.LAST_EDITED
        "
        class="reports-user-cell__edited-text"
        :class="{
          'text-sm text-neutral-text mb-0': hasCombinedCreateAndSavedReports,
        }"
      >
        {{
          `${hasCombinedCreateAndSavedReports ? '' : 'Edited '}${getRelativeEditTime}`
        }}
      </span>
      <div
        class="flex flex-row items-center"
        :class="{
          'gap-4': hasCombinedCreateAndSavedReports,
          'gap-8': !hasCombinedCreateAndSavedReports,
        }"
      >
        <BittsAvatar
          v-if="!hasCombinedCreateAndSavedReports"
          :user="user"
          :show-initials="true"
          size="x-small"
          class="leading-none"
        />
        <FontAwesomeIcon
          v-else
          :icon="['fak', 'person']"
          :class="{
            'h-8 w-8': type === COL_TYPES.LAST_EDITED,
            'h-12 w-12': type === COL_TYPES.OWNER,
          }"
          class="text-neutral-accent"
        />
        <span
          class="reports-user-cell__user-text"
          :class="{
            'text-xs text-neutral-text-weak':
              hasCombinedCreateAndSavedReports &&
              type === COL_TYPES.LAST_EDITED,
            'text-sm':
              hasCombinedCreateAndSavedReports && type === COL_TYPES.OWNER,
          }"
        >
          {{ `${user.first_name} ${user.last_name}` }}
        </span>
      </div>
    </div>
  </ReportCellWrapper>
</template>

<script setup lang="ts">
import { BittsAvatar } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { DateTime } from 'luxon';
import { computed } from 'vue';

import ReportCellWrapper from '@/components/reports/ReportCellWrapper.vue';

import { FEATURE_FLAG } from '@/constants/feature_flags';
import { ReportCell } from '@/interfaces/reports';
import { useFeatureFlagStore } from '@/stores';

const { params } = defineProps<{ params: ICellRendererParams<ReportCell> }>();

const { hasFeatureFlag } = useFeatureFlagStore();
const hasCombinedCreateAndSavedReports = computed(() =>
  hasFeatureFlag(FEATURE_FLAG.TEMP_CREATE_AND_SAVED_REPORTS),
);

const COL_TYPES = {
  OWNER: 'owner',
  LAST_EDITED: 'lastEdited',
} as const;
type ColTypes = (typeof COL_TYPES)[keyof typeof COL_TYPES];
const type = computed(() => params.colDef?.field as ColTypes);
const user = computed(() =>
  type.value === COL_TYPES.LAST_EDITED
    ? params.value.updatedByUser
    : params.value.createdByUser,
);

const getRelativeEditTime = computed(() =>
  hasCombinedCreateAndSavedReports.value
    ? DateTime.fromISO(params.value.report.updated_at).toFormat('DDD')
    : DateTime.fromISO(params.value.report.updated_at).toRelative(),
);
</script>

<style lang="pcss">
.reports-user-cell__edited-text {
  @apply text-sm text-neutral-800 leading-none mb-8;
}

.reports-user-cell__user-text {
  @apply text-base text-neutral-500 leading-none truncate;
}
</style>
